import { ShoeSizeTable } from '@components/ShoeSizeTable/ShoeSizeTable'
import { SizeGuideHeader } from '@components/SizeGuideHeader/SizeGuideHeader'
import { SizeGuideModalFooter } from '@components/SizeGuideModalFooter/SizeGuideModalFooter'
import type { ShoeSizeGuideJson } from '@velasca/types'
import { SansText } from '@velasca/ui/sans-text'
import { SansTitle } from '@velasca/ui/sans-title'
import { SerifTitle } from '@velasca/ui/serif-title'
import { buildIntrinsicWidthSources } from '@velasca/utils/build-intrinsic-width-sources'
import { sanitizeHTML } from '@velasca/utils/sanitize-html'
import { setQueryParams } from '@velasca/utils/set-query-params'
import {
  AdviceBlock,
  AdviceImage,
  ModalSeparator,
  ShoeAdvices,
  ShoeDescriptionWrap,
  TitleWrap,
} from './ShoeSizeGuide.styles'

export const ShoeSizeGuide = ({
  sizeGuide,
  defaultMeasurementSystem,
  translations,
}: ShoeSizeGuideJson) => {
  const {
    topTitle,
    topText,
    velascaSizeLabel,
    localizedMeasure01,
    localizedMeasure02,
    firstButtonText = 'Misure in CM',
    secondButtonText = 'Misure in Pollici',
    secondBlockTitle,
    secondBlockText,
    thirdBlockTitle,
    thirdBlockText,
    advicesTitle,
    advicesText,
    advice01Question,
    advice01Answer,
    advice01Image,
    advice02Question,
    advice02Answer,
    advice02Image,
    footerTitle,
    footerText,
    upperTitle,
  } = translations

  if (!sizeGuide) {
    return null
  }

  const { sizeGuideId, table } = sizeGuide

  return (
    <>
      {sizeGuideId && (
        <>
          <SizeGuideHeader title={topTitle} upperTitle={upperTitle} />

          <SansText center size="md" margin="0 0 4.5rem">
            {topText}
          </SansText>

          <ShoeSizeTable
            convertible
            firstCellFixed
            table={table}
            localizedSizesLabel={velascaSizeLabel}
            localizedMeasure01={localizedMeasure01}
            localizedMeasure02={localizedMeasure02}
            firstButtonText={firstButtonText}
            secondButtonText={secondButtonText}
            defaultMeasurementSystem={defaultMeasurementSystem}
          />

          <ShoeDescriptionWrap>
            {secondBlockTitle && (
              <SansTitle
                center
                as="h3"
                fontWeight="500"
                size="sm"
                color="primary"
                margin="0 0 2rem"
              >
                {secondBlockTitle}
              </SansTitle>
            )}
            {secondBlockText && (
              <SansText
                center
                as="div"
                dangerouslySetInnerHTML={sanitizeHTML(secondBlockText)}
              />
            )}

            {secondBlockText && thirdBlockText && <ModalSeparator />}

            {thirdBlockTitle && (
              <SansTitle
                center
                as="h3"
                fontWeight="500"
                size="sm"
                color="primary"
                margin="0 0 2rem"
              >
                {thirdBlockTitle}
              </SansTitle>
            )}
            {thirdBlockText && (
              <SansText
                center
                as="div"
                dangerouslySetInnerHTML={sanitizeHTML(thirdBlockText)}
              />
            )}
          </ShoeDescriptionWrap>

          {(advice01Answer || advice02Answer) && (
            <ShoeAdvices>
              <TitleWrap>
                <SerifTitle
                  center
                  as="h4"
                  size="md"
                  color="primary"
                  margin="0 0 1rem"
                >
                  {advicesTitle}
                </SerifTitle>
                <SansTitle
                  center
                  tracking="wide"
                  size="sm"
                  color="primary"
                  margin="0 0 3rem"
                  as="h5"
                >
                  {advicesText}
                </SansTitle>
              </TitleWrap>

              <AdviceBlock>
                {advice01Image && (
                  <AdviceImage
                    lazyload
                    lqip
                    alt={advice01Image?.alt}
                    imgSrc={setQueryParams(advice01Image?.src, [
                      { name: 'width', value: '30' },
                    ])}
                    sources={buildIntrinsicWidthSources({
                      src: advice01Image?.src,
                      maxWidth: advice01Image?.width,
                    })}
                  />
                )}
                {advice01Question && (
                  <SansTitle
                    center
                    as="div"
                    fontWeight="500"
                    size="xs"
                    color="primary"
                    margin="0 0 2rem"
                    dangerouslySetInnerHTML={sanitizeHTML(advice01Question)}
                  />
                )}
                {advice01Answer && (
                  <SansText
                    as="div"
                    dangerouslySetInnerHTML={sanitizeHTML(advice01Answer)}
                  />
                )}
              </AdviceBlock>

              <AdviceBlock>
                {advice02Image && (
                  <AdviceImage
                    lazyload
                    lqip
                    alt={advice02Image?.alt}
                    imgSrc={setQueryParams(advice02Image?.src, [
                      { name: 'width', value: '30' },
                    ])}
                    sources={buildIntrinsicWidthSources({
                      src: advice02Image?.src,
                      maxWidth: advice02Image.width,
                    })}
                  />
                )}
                {advice02Question && (
                  <SansTitle
                    center
                    as="div"
                    fontWeight="500"
                    size="xs"
                    color="primary"
                    margin="0 0 2rem"
                    dangerouslySetInnerHTML={sanitizeHTML(advice02Question)}
                  />
                )}
                {advice02Answer && (
                  <SansText
                    as="div"
                    dangerouslySetInnerHTML={sanitizeHTML(advice02Answer)}
                  />
                )}
              </AdviceBlock>
            </ShoeAdvices>
          )}
        </>
      )}
      <SizeGuideModalFooter title={footerTitle} text={footerText} />
    </>
  )
}
